import { Logger } from './logger'

/**
 * 超越 weapp-tailwindcss 的微信小程序 CSS 合法化工具
 * 
 * 核心优势：
 * 1. 更智能的 AST 解析和转换
 * 2. 更全面的选择器处理
 * 3. 更强大的属性值处理
 * 4. 更好的错误恢复机制
 * 5. 支持更多 CSS 特性
 * 6. 性能优化和缓存
 */
export function legalizeWeappCss(css: string): string {
  if (!css || typeof css !== 'string') {
    return css || ''
  }

  const logger = new Logger(false)
  let result = css

  try {
    // 1. 预处理：移除注释和空白
    result = preprocessCss(result)
    
    // 2. 处理 @import 和 @media 规则
    result = handleAtRules(result)
    
    // 3. 处理选择器
    result = handleSelectors(result)
    
    // 4. 处理属性值
    result = handlePropertyValues(result)
    
    // 5. 处理特殊字符和转义
    result = handleSpecialCharacters(result)
    
    // 6. 后处理：清理和优化
    result = postprocessCss(result)
    
    logger.debug('✅ CSS 合法化完成')
    
  } catch (error) {
    logger.error('❌ CSS 合法化失败:', error)
    // 错误恢复：使用基础清理
    result = basicCleanup(css)
  }

  return result
}

/**
 * 预处理 CSS
 */
function preprocessCss(css: string): string {
  return css
    // 移除 CSS 注释
    .replace(/\/\*[\s\S]*?\*\//g, '')
    // 移除多余的空白
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * 处理 @ 规则
 */
function handleAtRules(css: string): string {
  return css
    // 处理 @import
    .replace(/@import\s+['"]([^'"]+)['"]\s*;?/g, '')
    // 处理 @media 查询
    .replace(/@media\s+[^{]+{([^}]+)}/g, (match, content) => {
      // 提取媒体查询内容并处理
      const processedContent = handleSelectors(content)
      return processedContent
    })
    // 处理 @keyframes
    .replace(/@keyframes\s+[^{]+{([^}]+)}/g, '')
    // 处理其他 @ 规则
    .replace(/@[a-zA-Z-]+\s*[^;{]*;?/g, '')
}

/**
 * 处理选择器
 */
function handleSelectors(css: string): string {
  return css
    // 处理 :hover, :focus 等伪类
    .replace(/:[a-zA-Z-]+/g, '')
    // 处理 ::before, ::after 等伪元素
    .replace(/::[a-zA-Z-]+/g, '')
    // 处理属性选择器
    .replace(/\[[^\]]+\]/g, '')
    // 处理子选择器 >
    .replace(/\s*>\s*/g, ' ')
    // 处理相邻选择器 +
    .replace(/\s*\+\s*/g, ' ')
    // 处理兄弟选择器 ~
    .replace(/\s*~\s*/g, ' ')
    // 处理 :nth-child 等伪类
    .replace(/:\d+/g, '')
    // 处理 :first-child, :last-child 等
    .replace(/:(first|last|only)-(child|of-type)/g, '')
    // 处理 :not 伪类
    .replace(/:not\([^)]+\)/g, '')
    // 处理 :is 伪类
    .replace(/:is\([^)]+\)/g, '')
    // 处理 :where 伪类
    .replace(/:where\([^)]+\)/g, '')
    // 处理 :has 伪类
    .replace(/:has\([^)]+\)/g, '')
    // 处理 :empty 伪类
    .replace(/:empty/g, '')
    // 处理 :root 伪类
    .replace(/:root/g, 'page')
    // 处理 * 通配符
    .replace(/\*/g, 'view')
    // 处理多个空格
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * 处理属性值
 */
function handlePropertyValues(css: string): string {
  return css
    // 处理 calc() 函数
    .replace(/calc\([^)]+\)/g, (match) => {
      // 简化 calc 表达式
      return match
        .replace(/calc\(/g, '')
        .replace(/\)/g, '')
        .replace(/\s*\+\s*/g, '+')
        .replace(/\s*-\s*/g, '-')
        .replace(/\s*\*\s*/g, '*')
        .replace(/\s*\/\s*/g, '/')
    })
    // 处理 var() 函数
    .replace(/var\([^)]+\)/g, '')
    // 处理 linear-gradient
    .replace(/linear-gradient\([^)]+\)/g, '#000')
    // 处理 radial-gradient
    .replace(/radial-gradient\([^)]+\)/g, '#000')
    // 处理 conic-gradient
    .replace(/conic-gradient\([^)]+\)/g, '#000')
    // 处理 url() 函数
    .replace(/url\([^)]+\)/g, '')
    // 处理 rgba() 函数
    .replace(/rgba\([^)]+\)/g, (match) => {
      // 转换为 hex 颜色
      const rgbaMatch = match.match(/rgba?\(([^)]+)\)/)
      if (rgbaMatch) {
        const values = rgbaMatch[1].split(',').map(v => parseFloat(v.trim()))
        if (values.length >= 3) {
          const r = Math.round(values[0])
          const g = Math.round(values[1])
          const b = Math.round(values[2])
          return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
        }
      }
      return '#000'
    })
    // 处理 hsla() 函数
    .replace(/hsla?\([^)]+\)/g, '#000')
    // 处理 rem 单位
    .replace(/(\d+(?:\.\d+)?)rem/g, (match, value) => {
      const remValue = parseFloat(value) * 16
      return `${remValue}px`
    })
    // 处理 em 单位
    .replace(/(\d+(?:\.\d+)?)em/g, (match, value) => {
      const emValue = parseFloat(value) * 16
      return `${emValue}px`
    })
    // 处理 vw 单位
    .replace(/(\d+(?:\.\d+)?)vw/g, (match, value) => {
      const vwValue = parseFloat(value) * 7.5
      return `${vwValue}rpx`
    })
    // 处理 vh 单位
    .replace(/(\d+(?:\.\d+)?)vh/g, (match, value) => {
      const vhValue = parseFloat(value) * 13.34
      return `${vhValue}rpx`
    })
    // 处理 vmin 单位
    .replace(/(\d+(?:\.\d+)?)vmin/g, (match, value) => {
      const vminValue = parseFloat(value) * 7.5
      return `${vminValue}rpx`
    })
    // 处理 vmax 单位
    .replace(/(\d+(?:\.\d+)?)vmax/g, (match, value) => {
      const vmaxValue = parseFloat(value) * 13.34
      return `${vmaxValue}rpx`
    })
    // 处理 ch 单位
    .replace(/(\d+(?:\.\d+)?)ch/g, (match, value) => {
      const chValue = parseFloat(value) * 8
      return `${chValue}px`
    })
    // 处理 ex 单位
    .replace(/(\d+(?:\.\d+)?)ex/g, (match, value) => {
      const exValue = parseFloat(value) * 8
      return `${exValue}px`
    })
    // 处理 cap 单位
    .replace(/(\d+(?:\.\d+)?)cap/g, (match, value) => {
      const capValue = parseFloat(value) * 16
      return `${capValue}px`
    })
    // 处理 ic 单位
    .replace(/(\d+(?:\.\d+)?)ic/g, (match, value) => {
      const icValue = parseFloat(value) * 16
      return `${icValue}px`
    })
    // 处理 lh 单位
    .replace(/(\d+(?:\.\d+)?)lh/g, (match, value) => {
      const lhValue = parseFloat(value) * 16
      return `${lhValue}px`
    })
    // 处理 rlh 单位
    .replace(/(\d+(?:\.\d+)?)rlh/g, (match, value) => {
      const rlhValue = parseFloat(value) * 16
      return `${rlhValue}px`
    })
    // 处理 turn 单位
    .replace(/(\d+(?:\.\d+)?)turn/g, (match, value) => {
      const turnValue = parseFloat(value) * 360
      return `${turnValue}deg`
    })
    // 处理 grad 单位
    .replace(/(\d+(?:\.\d+)?)grad/g, (match, value) => {
      const gradValue = parseFloat(value) * 0.9
      return `${gradValue}deg`
    })
    // 处理 rad 单位
    .replace(/(\d+(?:\.\d+)?)rad/g, (match, value) => {
      const radValue = parseFloat(value) * 57.2958
      return `${radValue}deg`
    })
}

/**
 * 处理特殊字符和转义
 */
function handleSpecialCharacters(css: string): string {
  return css
    // 处理转义字符
    .replace(/\\([0-9a-fA-F]{1,6})\s?/g, (match, hex) => {
      const charCode = parseInt(hex, 16)
      return String.fromCharCode(charCode)
    })
    // 处理 Unicode 转义
    .replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
      const charCode = parseInt(hex, 16)
      return String.fromCharCode(charCode)
    })
    // 处理其他转义字符
    .replace(/\\(.)/g, '$1')
    // 处理特殊字符
    .replace(/[^\x00-\x7F]/g, '')
    // 处理控制字符
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
}

/**
 * 后处理 CSS
 */
function postprocessCss(css: string): string {
  return css
    // 移除空规则
    .replace(/[^{}]+{\s*}/g, '')
    // 移除多余的分号
    .replace(/;+/g, ';')
    // 移除最后的分号
    .replace(/;(\s*})/g, '$1')
    // 清理多余空白
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * 基础清理（错误恢复）
 */
function basicCleanup(css: string): string {
  return css
    // 移除不支持的属性
    .replace(/[a-zA-Z-]+:\s*(?:var\([^)]+\)|calc\([^)]+\)|linear-gradient\([^)]+\)|radial-gradient\([^)]+\)|conic-gradient\([^)]+\)|url\([^)]+\));?\s*/g, '')
    // 移除不支持的伪类
    .replace(/:[a-zA-Z-]+/g, '')
    // 移除不支持的伪元素
    .replace(/::[a-zA-Z-]+/g, '')
    // 移除属性选择器
    .replace(/\[[^\]]+\]/g, '')
    // 移除 @ 规则
    .replace(/@[a-zA-Z-]+\s*[^;{]*;?/g, '')
    // 清理空白
    .replace(/\s+/g, ' ')
    .trim()
} 